import {
    ClipboardDocumentListIcon,
    PaperClipIcon,
    VideoCameraIcon,
} from '@heroicons/react/24/outline';
import { CheckCircleIcon } from '@heroicons/react/24/solid';
import { useCallback } from 'react';
import { capitalizarPalabras } from '../../../common/upperCaseWord';
import { Avatar } from '../../../components/avatar';
import { isCourseZoomEnabled } from '../../../constants/courseZoom';
import {
    EVENT_KINDS,
    COURSE_PROGRESS_DOTS,
    courseLabelDisplay,
    formatCourseDateRange,
    getCourseProgressDots,
    getCourseProgressPercent,
    teacherFirstName,
    teacherName,
} from './calendarEvents';
import { useCalendarEventExpand } from './hooks/useCalendarEventExpand';

const PROGRESS_DOT_INDICES = Array.from(
    { length: COURSE_PROGRESS_DOTS },
    (_, index) => index + 1,
);



/**

 * Tarjeta de clase en la rejilla semanal o chip compacto en vista mes.

 */

export const CalendarEventCard = ({

    event,

    variant = 'default',

    onOpenDetail,

    onCloseDetail,

}) => {

    const { kind, course, classSession } = event.resource || {};

    const isTask = kind === EVENT_KINDS.TASK;

    const isMonthView = variant === 'month';

    const live = !isTask && classSession?.temporalStatus === 'live';

    const courseDates = formatCourseDateRange(course);

    const teacher = teacherName(course?.teacher);

    const teacherShort = capitalizarPalabras(teacherFirstName(course?.teacher));

    const displayTitle = isTask ? event.title : courseLabelDisplay(course);

    const progressPercent = getCourseProgressPercent(course);

    const progressDots = getCourseProgressDots(course);

    const pendingTasks = course?.stats?.pendingTasks ?? 0;

    const filesCount = course?.stats?.files ?? 0;

    const showProgress = Boolean(course?.fromDate && course?.toDate);

    const hasZoom = isCourseZoomEnabled(course);

    const levelTag = course?.level?.name

        ? capitalizarPalabras(course.level.name)

        : null;



    const {

        cardRef,

        expanded,

        expandCard,

        collapseExpanded,

    } = useCalendarEventExpand();



    const handleCardPointerEnter = useCallback((nativeEvent) => {

        expandCard();

        onOpenDetail?.(nativeEvent);

    }, [expandCard, onOpenDetail]);



    const handleCardPointerLeave = useCallback((nativeEvent) => {

        collapseExpanded();

        onCloseDetail?.(nativeEvent);

    }, [collapseExpanded, onCloseDetail]);



    const pointerProps = onOpenDetail ? {

        onMouseEnter: onOpenDetail,

        onMouseLeave: onCloseDetail,

        onFocus: onOpenDetail,

        onBlur: onCloseDetail,

    } : {};



    if (isTask) {
        const submitted = Boolean(event.resource?.task?.submitted);
        const graded = Boolean(event.resource?.task?.graded);

        if (isMonthView) {
            return (
                <div className="cal-month-chip cal-month-chip--task" {...pointerProps}>
                    <span className="cal-month-chip__label">{event.title}</span>
                    {submitted ? (
                        <CheckCircleIcon className="cal-task-chip__check" aria-label="Tarea entregada" />
                    ) : null}
                    {graded ? (
                        <CheckCircleIcon className="cal-task-chip__check" aria-label="Tarea calificada" />
                    ) : null}
                </div>
            );
        }

        return (
            <div className="cal-task-chip" {...pointerProps}>
                <p className="cal-task-chip__title">{event.title}</p>
                <span className="cal-task-chip__checks">
                    {submitted ? (
                        <CheckCircleIcon className="cal-task-chip__check" aria-label="Tarea entregada" />
                    ) : null}
                    {graded ? (
                        <CheckCircleIcon className="cal-task-chip__check" aria-label="Tarea calificada" />
                    ) : null}
                </span>
            </div>
        );
    }



    if (isMonthView) {

        const identifier = course?.identifier || courseLabelDisplay(course);

        return (

            <div className="cal-month-chip" {...pointerProps}>

                <span className="cal-month-chip__label" translate="no">

                    {identifier}

                </span>

                {hasZoom ? (
                    <VideoCameraIcon
                        className="cal-month-chip__zoom"
                        title="Videollamadas Zoom"
                        aria-label="Con Zoom"
                    />
                ) : null}

            </div>

        );

    }



    return (

        <div

            ref={cardRef}

            className={`cal-event-card${live ? ' cal-event-card--live' : ''}${expanded ? ' cal-event-card--expanded' : ''}`}

            onMouseEnter={handleCardPointerEnter}

            onMouseLeave={handleCardPointerLeave}

        >

            <div className="cal-event-card__top">

                <div className="cal-event-card__tags">

                    {course?.identifier ? (

                        <span className="cal-event-card__tag" translate="no">

                            {course.identifier}

                        </span>

                    ) : null}

                    {levelTag ? (

                        <span className="cal-event-card__tag">{levelTag}</span>

                    ) : null}

                </div>

                {live ? (

                    <div className="cal-event-card__actions">

                        <span className="cal-event-card__live-pill">

                            <span className="cal-event-card__live-dot" aria-hidden="true" />

                            En vivo

                        </span>

                    </div>

                ) : null}

            </div>



            <header className="cal-event-card__header">

                <p className="cal-event-card__title">{displayTitle}</p>

                {courseDates ? (

                    <p className="cal-event-card__dates">{courseDates}</p>

                ) : null}

            </header>



            {showProgress ? (

                <div className="cal-event-card__progress-block">

                    <div className="cal-event-card__progress-head">

                        <span className="cal-event-card__progress-label">Progreso</span>

                        <span className="cal-event-card__progress-value">{progressPercent}%</span>

                    </div>

                    <div

                        className="cal-event-card__dots"

                        role="progressbar"

                        aria-valuenow={progressPercent}

                        aria-valuemin={0}

                        aria-valuemax={100}

                        aria-label={`Progreso del curso: ${progressPercent}%`}

                    >

                        {PROGRESS_DOT_INDICES.map((dot) => (

                            <span

                                key={dot}

                                className={`cal-event-card__dot${

                                    progressDots >= dot ? ' cal-event-card__dot--filled' : ''

                                }`}

                                aria-hidden="true"

                            />

                        ))}

                    </div>

                </div>

            ) : null}



            <div className="cal-event-card__spacer" aria-hidden="true" />



            <div className="cal-event-card__bottom">

                {teacher ? (

                    <div className="cal-event-card__profile">

                        <Avatar

                            size="sm"

                            firstName={course?.teacher?.firstName}

                            lastName={course?.teacher?.lastName}

                            name={teacher}

                            className="cal-event-card__avatar"

                        />

                        <span className="cal-event-card__profile-name">{teacherShort}</span>

                    </div>

                ) : (

                    <span aria-hidden="true" />

                )}



                <footer className="cal-event-card__stats">

                    {hasZoom ? (
                        <span
                            className="cal-event-card__stat cal-event-card__stat--zoom"
                            title="Videollamadas Zoom"
                        >
                            <VideoCameraIcon
                                className="cal-event-card__stat-icon"
                                aria-hidden="true"
                            />
                            <span className="sr-only">Con Zoom</span>
                        </span>
                    ) : null}

                    <span

                        className="cal-event-card__stat"

                        title="Tareas pendientes"

                    >

                        <ClipboardDocumentListIcon

                            className="cal-event-card__stat-icon"

                            aria-hidden="true"
                        />

                        <span className="cal-event-card__stat-value">{pendingTasks}</span>

                    </span>

                    <span

                        className="cal-event-card__stat"

                        title="Archivos del curso"

                    >

                        <PaperClipIcon

                            className="cal-event-card__stat-icon"

                            aria-hidden="true"
                        />

                        <span className="cal-event-card__stat-value">{filesCount}</span>

                    </span>

                </footer>

            </div>

        </div>

    );

};


